import { apiService } from './apiService';
import { ProposalResolveResult, ProposalState } from '../models/clinicAgent';
import { DAY_NAMES } from '../utils/weekDates';
import { API_BASE_URL } from '../config/env';

// Human, Spanish titles for proposal cards.
const ACTION_TITLES: Record<string, string> = {
  create_diary_item: 'Crear elemento de diario',
  update_diary_item: 'Actualizar elemento de diario',
  create_meal: 'Crear comida',
  create_ingredient: 'Crear ingrediente',
  create_reminder: 'Crear recordatorio',
  set_patient_activation: 'Cambiar activación del paciente',
  send_welcome_email: 'Enviar email de bienvenida',
};

// A bulk date-window edit (the agent's propose_diary_item_dates tool) arrives as
// one ordinary update_diary_item proposal per diary item whose payload carries
// only the item id and the new window — no name, type, subtype or schedule. It
// reads as a different kind of edit, so cards title and render it as such.
const DATE_WINDOW_KEYS = new Set(['effective_date', 'cancellation_date']);

export function isDatesOnlyPayload(payload?: Record<string, unknown> | null): boolean {
  if (!payload) return false;
  const keys = Object.keys(payload).filter((k) => k !== 'diary_item_id');
  return keys.length > 0 && keys.every((k) => DATE_WINDOW_KEYS.has(k));
}

export function proposalTitle(action: string, payload?: Record<string, unknown> | null): string {
  if (action === 'update_diary_item' && isDatesOnlyPayload(payload)) {
    return 'Actualizar fechas del elemento';
  }
  return ACTION_TITLES[action] || action;
}

// Whether this proposal can be accepted/rejected. Unknown / generic fallback
// actions (e.g. propose_clinic_action) are rendered read-only with no buttons.
export function isApplicable(action: string): boolean {
  return action in ACTION_TITLES;
}

// One accept/reject decision for POST /api/clinic/proposals/resolve/.
export interface ResolveDecision {
  proposal_id: string;
  decision: 'accept' | 'reject';
}

// Resolve one or more proposals. The backend executes the action (creates the
// diary item, meal, reminder, …) and persists the new state — the frontend no
// longer applies anything itself, it only sends the decision.
//
// The endpoint returns 200 when every item succeeded and 422 when any failed;
// both carry per-item results in the body (inspect `ok`/`status`/`error` per
// item). apiService throws on 422, so we unwrap its body and return the results;
// genuine transport/auth failures re-throw.
export async function resolveProposals(decisions: ResolveDecision[]): Promise<ProposalResolveResult[]> {
  try {
    const data = await apiService.post(`${API_BASE_URL}/clinic/proposals/resolve/`, { results: decisions });
    return Array.isArray(data?.results) ? (data.results as ProposalResolveResult[]) : [];
  } catch (err: any) {
    if (err?.status === 422 && Array.isArray(err?.response?.results)) {
      return err.response.results as ProposalResolveResult[];
    }
    throw err;
  }
}

// ----------------------------------------------------------------------------
// Day grouping
//
// A diary plan repeats the same meal / activity / supplement across several
// days, so the agent emits one proposal per day whose payloads are identical
// except for `day_of_week` (and, on updates, the per-day `diary_item_id`).
// We fold those into a single card and surface only the days, so a 7-day plan
// reads as one item with seven chips instead of seven near-identical cards.
// ----------------------------------------------------------------------------

// Only diary create/update proposals follow the one-row-per-day pattern.
const GROUPABLE_ACTIONS = new Set(['create_diary_item', 'update_diary_item']);

// Fields that legitimately differ between days of the same logical item and so
// must be excluded from the "is this the same proposal?" signature.
const PER_DAY_KEYS = new Set(['day_of_week', 'diary_item_id']);

// Deterministic JSON with object keys sorted, so two payloads that differ only
// in key order still hash to the same signature.
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

// A signature shared by proposals that are "the same item on a different day",
// or null when the proposal can't take part in day grouping (wrong action, or
// no day_of_week to vary on). Scoped to the producing run so two separate agent
// turns that happen to emit identical plans never collapse together. Note the
// per-day diary_item_id lives in `detail`, not `payload`, so it never enters the
// signature; we still strip it from payload defensively.
function daySignature(p: ProposalState): string | null {
  const payload = p.payload || {};
  if (!GROUPABLE_ACTIONS.has(p.action)) return null;
  if (payload.day_of_week == null) return null;
  const rest: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (!PER_DAY_KEYS.has(k)) rest[k] = v;
  }
  return `${p.run_id ?? 'norun'}|${p.action}|${stableStringify(rest)}`;
}

// A render unit for the proposal list: either a single proposal (`grouped`
// false) or several days of the same item folded together (`grouped` true,
// `proposals` sorted Monday→Sunday). Order follows each group's first day's
// position in the source list, with singletons kept in place.
export interface ProposalUnit {
  key: string;
  grouped: boolean;
  proposals: ProposalState[];
}

const DAY_ORDER = new Map(DAY_NAMES.map((d, i) => [d, i]));

function unitKey(proposals: ProposalState[]): string {
  return proposals.map((p, i) => p.id ?? `idx${i}`).join('+');
}

// Fold a message's proposals into render units, grouping same-item-different-day
// proposals. A lone day does not form a group.
export function groupProposalsByDay(proposals: ProposalState[]): ProposalUnit[] {
  const units: { sig: string | null; proposals: ProposalState[] }[] = [];
  const bySig = new Map<string, { sig: string | null; proposals: ProposalState[] }>();

  for (const p of proposals) {
    const sig = daySignature(p);
    if (sig != null && bySig.has(sig)) {
      bySig.get(sig)!.proposals.push(p);
      continue;
    }
    const unit = { sig, proposals: [p] };
    units.push(unit);
    if (sig != null) bySig.set(sig, unit);
  }

  return units.map((u) => {
    const grouped = u.proposals.length > 1;
    const ordered = grouped
      ? [...u.proposals].sort(
          (a, b) =>
            (DAY_ORDER.get(String(a.payload?.day_of_week)) ?? 99) -
            (DAY_ORDER.get(String(b.payload?.day_of_week)) ?? 99),
        )
      : u.proposals;
    return { key: unitKey(ordered), grouped, proposals: ordered };
  });
}
